import React, {
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import SpecialPriceProductSection from '@wconcept/wconcept-ui-renewal/components/organisms/_section/SpecialPriceProductSection';
import DateTabItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/DateTabItem';
import MenuBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MenuBarItem';
import useRenderProducts from '@wconcept/wconcept-display/hooks/useRenderProducts';
import useMyHeartItem from '@wconcept/wconcept-display/hooks/useMyHeartItem';
import useGlobalState from '@hooks/useGlobalState';
import {
  PromotionSchedule,
  PeriodType,
  PromotionScheduleTab,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { useServerTime, useServerTimeActions } from '@store/serverTimeStore';
import useGa4Event from '@hooks/useGa4Event';
import PrdItemSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/PrdItemSkeleton';
import { usePromotionTabProducts } from '@wconcept/wconcept-display/hooks/usePromotionTabProducts';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useBrazeLogger from '@hooks/useBrazeLogger';
import dayjs from 'dayjs';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';

interface SpecialPriceModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  selectedStartDate?: string;
  selectedEndDate?: string;
  subTitle?: string;
  title?: string;
  promotionId: number;
  periodType: PeriodType;
  schedules: PromotionSchedule[];
  contentId?: string;
  promotionTitle: string;
  isVisible?: boolean;
}
const MAX_COUNT = 30;
const INIT_MAX_COUNT = 12;

function SpecialPriceModule({
  bgType,
  visualTokens,
  isDesktop,
  selectedStartDate,
  selectedEndDate,
  subTitle,
  title,
  periodType,
  schedules,
  promotionId,
  contentId,
  promotionTitle,
  isVisible,
}: SpecialPriceModuleProps) {
  const { getAdjustedTime, isCurrentDateInRange } = useServerTimeActions();

  const {
    presetBgType,
    customColorsFull,
    productUnitColorType: promotionProductUnitColorType,
    productSkeletonBgType: promotionProductSkeletonBgType,
  } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const sectionColorType = customColorsFull ?? presetBgType;
  const productSkeletonKeyList = useMemo(
    () =>
      Array.from(
        { length: 12 },
        (_, index) => `special-price-skeleton-${index}`
      ),
    []
  );

  // tabDisplayEnabled  상품이 없으면 스케쥴 제거 false면 tabs에 상품 없으면 뺴주기
  const validSchedules = useMemo(
    () =>
      schedules?.filter(schedule => {
        // tabDisplayEnabled가 false이면 tabs에 상품이 없으면 제거

        if (schedule.tabDisplayEnabled) {
          // tabs가 있고, 각 tab에 상품이 있는지 확인
          if (schedule.tabs?.length) {
            const validTabs = schedule.tabs.filter(
              tab => tab.itemCdList?.length && tab.itemCdList.length > 2
            );

            return validTabs.length > 1;
          }
          // tabs가 없으면 itemCdList로 확인
          return false;
        }
        // tabDisplayEnabled가 true이면 상품이 있으면 유효
        return schedule.itemCdList?.length && schedule.itemCdList.length > 2;
      }),
    [schedules]
  );

  // 선택 상태
  // const [selectedTab, setSelectedTab] = useState<PromotionScheduleTab | null>(
  //   schedules?.[0]?.tabs?.[0] || null
  // );

  // 실제로 표시할 탭 (isSettledAll이 true일 때만 업데이트)
  // const [displayTab, setDisplayTab] = useState<PromotionScheduleTab | null>(
  //   schedules?.[0]?.tabs?.[0] || null
  // );

  // SSR/CSR 첫 렌더의 시간 계산 차이로 hydration mismatch가 발생하지 않도록
  // 초기값은 데이터 순서 기준으로 고정하고, 현재 기간 스케줄 보정은 mount 이후 처리한다.
  const [selectedSchedule, setSelectedSchedule] =
    useState<PromotionSchedule | null>(validSchedules?.[0] ?? null);
  const [selectedCategoryTab, setSelectedCategoryTab] =
    useState<PromotionScheduleTab | null>(null);

  // const { onClickHeart } = useMyHeartItem({
  //   isLogin: !!assistData?.isLogin,
  // });

  const [maxCount, setMaxCount] = useState(INIT_MAX_COUNT);

  // 활성 스케줄의 상품만 조회 - 스케줄 변경 시 자동으로 해당 스케줄 상품 fetch
  const activeScheduleItemCodes = useMemo(() => {
    if (!selectedSchedule) return [];
    const codes = [
      ...(selectedSchedule.itemCdList || []),
      ...(selectedSchedule.tabs?.flatMap(t => t.itemCdList || []) || []),
    ];
    return [...new Set(codes)];
  }, [selectedSchedule?.scheduleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { productsMap: tabProductsMap, isSuccess: isTabProductsSuccess } =
    usePromotionTabProducts({
      contentId: contentId || '',
      tabKey: selectedSchedule?.scheduleId?.toString() || '',
      itemCodes: activeScheduleItemCodes,
      itemStatusCd: ['04'],
      productType: 'SECRET',
      enabled: isVisible && !!selectedSchedule,
    });

  const { sendGa4AddToWishProduct } = useGa4Event();
  const { sendMolocoAddToWish } = useMolocoLogger();
  const { sendBrazeRenewalMyHeart } = useBrazeLogger();
  const { sendGa4TabClick, sendGa4SelectItem, sendGa4ButtonClick } =
    useGa4Event();

  // tabMenuList 생성 조건 확인 함수
  const hasTabMenuList = useCallback(
    () => periodType === 'DAILY' && (validSchedules?.length ?? 0) > 0,
    [periodType, validSchedules?.length]
  );

  const getFinalPageNo = useCallback((): number | undefined => {
    const pageNo = schedules?.findIndex(
      schedule => schedule.scheduleId === selectedSchedule?.scheduleId
    );
    // tabMenuList가 있을 때는 카테고리 탭 유무와 상관없이 pageNo 반환
    const hasDailyTab = hasTabMenuList();

    // 일자 탭이 노출될 때는 카테고리 탭 유무와 상관없이 pageNo 반환
    if (hasDailyTab) {
      if (pageNo !== undefined && pageNo !== -1) {
        return pageNo + 1;
      }
      return undefined;
    }
    // 일자 탭이 없을 때는 기존 로직대로
    if (pageNo !== undefined && pageNo !== -1) {
      return pageNo + 1;
    }
    return undefined;
  }, [schedules, selectedSchedule?.scheduleId, hasTabMenuList]);

  const { renderProducts } = useRenderProducts({
    doAfterAddMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: true,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        area: 'promotion_specialoffer_myheart',
        issueCd: promotionId.toString(),
        issueNm: promotionTitle,
        tabNm: selectedCategoryTab?.tabName,
        pageNo: getFinalPageNo(),
        variable: title,
      });
      sendMolocoAddToWish({
        itemCd: product.itemCd,
        amount: product.finalPrice,
        pageId: window.location.pathname,
      });
      sendBrazeRenewalMyHeart({
        product,
        pageId: window.location.pathname,
        useGender: true,
        defaultGender: 'women',
      });
      // onClickHeart?.(product, true, index || 0);
    },
    doAfterDeleteMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: false,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        area: 'promotion_specialoffer_myheart',
        issueCd: promotionId.toString(),
        issueNm: promotionTitle,
        tabNm: selectedCategoryTab?.tabName,
        pageNo: getFinalPageNo(),
        variable: title,
      });
      // onClickHeart?.(product, false, index || 0);
    },
  });

  useLayoutEffect(() => {
    if (selectedSchedule?.tabDisplayEnabled) {
      setSelectedCategoryTab(selectedSchedule?.tabs?.[0] || null);
    }
  }, []);

  // serverTimeOffset이 초기화된 후 selectedSchedule 재설정
  // const { serverTimeOffset } = useServerTime();
  useLayoutEffect(() => {
    setTimeout(() => {
      if (validSchedules?.length) {
        const currentSchedule = validSchedules.find(schedule =>
          isCurrentDateInRange(
            schedule.displayStartDate,
            schedule.displayEndDate
          )
        );
        if (
          currentSchedule &&
          currentSchedule.scheduleId !== selectedSchedule?.scheduleId
        ) {
          setSelectedSchedule(currentSchedule);

          if (currentSchedule.tabDisplayEnabled) {
            setSelectedCategoryTab(currentSchedule.tabs[0]);
          }
        }
      }
    }, 200);
  }, [validSchedules]);

  // 현재 탭의 순서보존 데이터
  const orderedProducts = useMemo(() => {
    if (!selectedSchedule?.tabDisplayEnabled) {
      return selectedSchedule?.itemCdList
        ?.map(itemCd => tabProductsMap.get(itemCd))
        .filter((p): p is Product => !!p);
    }
    if (!selectedCategoryTab?.itemCdList?.length) return [];
    return selectedCategoryTab.itemCdList
      .map(itemCd => tabProductsMap.get(itemCd))
      .filter((p): p is Product => !!p);
  }, [tabProductsMap, selectedSchedule, selectedCategoryTab]);

  // displayTab을 사용하므로 기존 displayProducts 로직은 제거
  const getDisplayDate = (date: string) => {
    const today = dayjs().format('YYYY-MM-DD');
    if (date === today) {
      return `오늘${dayjs(date).format('MM.DD')}`;
    }
    return `${dayjs(date).format('ddd')} ${dayjs(date).format('MM.DD')}`;
  };

  // 이벤트 기간 탭 핸들러
  const handleScheduleChange = useCallback(
    (schedule: PromotionSchedule, index: number) => {
      // promotion_specialoffer_tab
      sendGa4TabClick({
        area: 'promotion_specialoffer_tab',
        page: 'promotion',
        // ex 오늘이면 - 오늘 4.8, 다른날짜면 - 금 8.22
        text: getDisplayDate(schedule.displayStartDate),
        listNo: index + 1,
        variable: title,
      });
      setMaxCount(INIT_MAX_COUNT);
      setSelectedSchedule(schedule);
      if (schedule.tabDisplayEnabled) {
        setSelectedCategoryTab(schedule.tabs[0]);
      }
    },
    [sendGa4TabClick]
  );

  // 카테고리 탭 핸들러
  const handleTabChange = useCallback(
    (tab: PromotionScheduleTab, index: number) => {
      sendGa4TabClick({
        area: 'promotion_specialoffer_category_tab',
        text: tab.tabName,
        listNo: index + 1,
        variable: title,
      });
      setMaxCount(INIT_MAX_COUNT);
      if (selectedSchedule?.tabDisplayEnabled) {
        setSelectedCategoryTab(tab);
      }
    },
    [sendGa4TabClick, selectedSchedule?.tabDisplayEnabled]
  );

  // 일자 탭 렌더링
  const tabMenuList = useMemo(() => {
    if (periodType !== 'DAILY' || !validSchedules?.length) return [];
    return validSchedules?.map((schedule, index) => (
      <DateTabItem
        key={schedule.scheduleId}
        serverTime={getAdjustedTime()}
        startDate={schedule.displayStartDate}
        endDate={schedule.displayEndDate}
        value={schedule.scheduleId.toString()}
        selectedValue={selectedSchedule?.scheduleId.toString() || ''}
        colorType={sectionColorType}
        onClick={() => handleScheduleChange(schedule, index)}
      />
    ));
  }, [
    periodType,
    validSchedules,
    getAdjustedTime,
    selectedSchedule,
    handleScheduleChange,
  ]);
  // 카테고리 탭 렌더링
  const categoryMenuList = useMemo(() => {
    if (!selectedSchedule?.tabDisplayEnabled) return [];

    if (!selectedSchedule?.tabs?.length || selectedSchedule?.tabs?.length < 2)
      return [];

    return selectedSchedule.tabs.slice(0, 15).map((tab, index) => (
      <MenuBarItem
        key={tab.tabId.toString()}
        value={tab.tabId.toString()}
        text={tab.tabName}
        // selectedValue={displayTab?.tabId?.toString() || ''}
        onClick={() => handleTabChange(tab, index)}
      />
    ));
  }, [selectedSchedule, handleTabChange]);

  // 상품 렌더링
  const renderedProducts = useCallback(() => {
    if (
      !isDesktop &&
      (!isTabProductsSuccess || orderedProducts?.length === 0)
    ) {
      return productSkeletonKeyList.map(skeletonKey => (
        <PrdItemSkeleton
          key={skeletonKey}
          bgType={promotionProductSkeletonBgType}
        />
      ));
    }

    if (orderedProducts?.length && orderedProducts?.length > 0) {
      return renderProducts({
        type: 'simple',
        maxCount,
        rs: isDesktop ? '417' : '216',
        products: orderedProducts,
        isBrandNameKr: true,
        colorType: promotionProductUnitColorType,
        onClickProduct: (product, index) => {
          sendGa4SelectItem({
            area: 'promotion_specialoffer_item',
            text: product.itemName,
            listNo: index + 1,
            ecommercePath: '프로모션',
            product,
            tabNm: selectedCategoryTab?.tabName,
            pageNo: getFinalPageNo(),
            variable: title,
          });
        },
      });
    }
    return [];
  }, [
    isDesktop,
    isTabProductsSuccess,
    orderedProducts,
    productSkeletonKeyList,
    promotionProductSkeletonBgType,
    promotionProductUnitColorType,
    renderProducts,
    maxCount,
    getFinalPageNo,
    sendGa4SelectItem,
    selectedCategoryTab?.tabName,
  ]);
  // 1. 카테고리 탭 전시 조건
  // - 탭 내에 노출 가능한 상품 3개 이상일 때만 탭 전시
  // - 3개 미만인 경우, 탭 비전시 처리
  // 2. 일자 탭 전시 조건
  // -  카테고리 탭 = 전시 : 해당 일자에 노출 가능한 카테고리 탭이 3개 미만인 경우, 탭 비전시 처리
  // -  카테고리 탭 = 비전시 : 해당 일자에 노출 가능한 상품이 3개 미만인 경우, 탭 비전시 처리
  // 3. 모듈 전시 조건
  // - 기간 유형 = 전체&카테고리 탭 = 전시 : 노출 가능한 카테고리 탭이 3개 미만인 경우, 모듈 비전시 처리
  // - 기간 유형 = 전체&카테고리 탭 = 비전시 : 노출 가능한 상품이 3개 미만인 경우, 모듈 비전시 처리
  // - 기간 유형 = 일별&카테고리 탭 = 전시 : 노출 가능한 일자 탭이 3개 미만인 경우, 모듈 비전시 처리
  // - 기간 유형 = 일별&카테고리 탭 = 비전시 : 노출 가능한 일자 탭이 3개 미만인 경우, 모듈 비전시 처리
  const hasMoreToShow =
    orderedProducts?.length && orderedProducts?.length > maxCount;

  if (
    periodType === 'ALL' &&
    selectedSchedule?.tabDisplayEnabled &&
    selectedSchedule?.tabs?.length &&
    selectedSchedule?.tabs?.length < 2
  ) {
    return null;
  }

  if (
    periodType === 'ALL' &&
    selectedSchedule?.tabDisplayEnabled &&
    selectedSchedule?.tabs?.length &&
    selectedSchedule?.tabs?.length < 2
  ) {
    return null;
  }

  if (
    periodType === 'ALL' &&
    selectedSchedule?.tabDisplayEnabled &&
    (!selectedCategoryTab?.itemCdList?.length ||
      selectedCategoryTab?.itemCdList?.length < 3)
  ) {
    return null;
  }
  if (periodType === 'DAILY' && validSchedules?.length < 2) return null;

  if (validSchedules?.length === 0) return null;

  return (
    <SpecialPriceProductSection
      colorType={sectionColorType}
      title={title}
      subTitle={subTitle}
      startDate={selectedSchedule?.displayStartDate}
      endDate={selectedSchedule?.displayEndDate}
      tabMenuList={tabMenuList}
      selDateTab={selectedSchedule?.scheduleId.toString() || ''}
      selectedTabId={selectedSchedule?.scheduleId.toString() || ''}
      categoryTabList={categoryMenuList}
      selectedCateTabId={selectedCategoryTab?.tabId?.toString() || ''}
      productList={renderedProducts()}
      serverTime={getAdjustedTime()}
      isDesktop={isDesktop}
      isMore={!hasMoreToShow}
      isLink={false}
      onClickMore={() => {
        if (!hasMoreToShow) return;
        setMaxCount(MAX_COUNT);
        sendGa4ButtonClick({
          area: 'promotion_specialoffer_more_button',
          text: '상품 더보기',
          page: 'promotion',
          variable: title,
        });
      }}
    />
  );
}

export default memo(SpecialPriceModule);
